import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Marquee } from "@/components/Layout";
import HeroSearch from "@/components/HeroSearch";
import { useT } from "@/context/PreferencesContext";
import { ArrowRight, Lightning, Package, Storefront } from "@phosphor-icons/react";

const HERO_IMG = "/hero/hero-racing-bw.jpg";
const CAT_IMG = "https://images.pexels.com/photos/29621501/pexels-photo-29621501.jpeg";
const ARTIST_IMG = "https://images.unsplash.com/photo-1415886541506-6efc5e4b1786";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [artists, setArtists] = useState([]);
  const t = useT();

  useEffect(() => {
    api.get("/products/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/artists", { params: { featured: true, limit: 6 } })
      .then((r) => {
        const items = r.data.items || [];
        if (items.length > 0) return setArtists(items.slice(0, 6));
        return api.get("/artists", { params: { limit: 6 } })
          .then((r2) => setArtists((r2.data.items || []).slice(0, 6)));
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden border-b border-white/10">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="relative max-w-[1600px] mx-auto w-full px-6 lg:px-10 pb-16 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00] mb-4">{t("hero.tagline")}</div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] uppercase leading-[0.9] max-w-5xl whitespace-pre-line">
            {t("hero.title")}
          </h1>
          <p className="mt-8 max-w-lg text-white/70 text-base">
            {t("hero.desc")}
          </p>

          <div className="mt-8">
            <HeroSearch />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/catalogue" data-testid="hero-shop-btn" className="btn-brutal inline-flex items-center gap-2 px-8 py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500]">
              {t("hero.discover")} <ArrowRight size={16} />
            </Link>
            <Link to="/register" data-testid="hero-artist-btn" className="btn-brutal inline-flex items-center gap-2 px-8 py-4 border border-white/30 hover:border-white text-white uppercase tracking-widest">
              {t("hero.sell")}
            </Link>
          </div>
        </div>
      </section>

      <Marquee text="NOUVELLE COLLECTION" />

      {/* Categories */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-baseline justify-between mb-10">
          <h2 className="font-display text-5xl md:text-7xl uppercase">Catégories</h2>
          <Link to="/catalogue" className="text-xs uppercase tracking-widest hover:text-[#FF3B00]">Tout voir →</Link>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { key: "hoodie", label: "Hoodies", img: "https://images.unsplash.com/photo-1680292783974-a9a336c10366" },
            { key: "tshirt", label: "T-Shirts", img: "https://images.unsplash.com/photo-1614214191247-5b2d3a734f1b" },
            { key: "cap", label: "Casquettes", img: CAT_IMG },
            { key: "limited", label: "Éditions Ltd.", img: "https://images.unsplash.com/photo-1633292750937-120a94f5c2bb" },
          ].map((c) => (
            <Link
              key={c.key}
              to={`/catalogue?category=${c.key}`}
              data-testid={`cat-${c.key}`}
              className="group relative aspect-[4/5] overflow-hidden border border-white/10 hover:border-[#FF3B00] transition-colors"
            >
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="font-display text-4xl uppercase">{c.label}</div>
                <div className="text-xs uppercase tracking-widest text-[#FF3B00] mt-2">Explorer →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-baseline justify-between mb-10">
            <h2 className="font-display text-5xl md:text-7xl uppercase">Tendances</h2>
            <Link to="/catalogue" className="text-xs uppercase tracking-widest hover:text-[#FF3B00]">Catalogue complet →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Featured artists */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-baseline justify-between mb-10">
          <h2 className="font-display text-5xl md:text-7xl uppercase">Pilotes en vedette</h2>
          <Link to="/pilotes" className="text-xs uppercase tracking-widest hover:text-[#FF3B00]">Tous les pilotes →</Link>
        </div>
        {artists.length === 0 ? (
          <div className="border border-white/10 p-16 text-center text-white/50">
            Aucun pilote pour le moment. <Link to="/register" className="text-[#FF3B00] underline">Soyez le premier.</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {artists.map((a) => (
              <Link key={a.id} to={`/pilotes/${a.id}`} className="border border-white/10 hover:border-[#FF3B00] p-6 transition-colors relative" data-testid={`artist-${a.id}`}>
                {a.featured && (
                  <div className="absolute top-2 right-2 bg-[#FF3B00] text-white text-[9px] uppercase tracking-widest px-2 py-0.5">Vedette</div>
                )}
                <div className="aspect-square bg-[#111] mb-4 overflow-hidden flex items-center justify-center font-display text-4xl">
                  {a.avatar_url ? (
                    <img src={a.avatar_url} alt={a.artist_name || a.name} className="w-full h-full object-cover" />
                  ) : (
                    (a.artist_name || a.name || "?")[0].toUpperCase()
                  )}
                </div>
                <div className="font-display text-xl uppercase leading-tight">{a.name || a.artist_name}</div>
                {a.artist_name && a.name && a.artist_name.toLowerCase() !== a.name.toLowerCase() && (
                  <div className="text-xs text-[#FF3B00] uppercase tracking-widest mt-1">« {a.artist_name} »</div>
                )}
                <div className="text-xs text-white/50 mt-2">{a.product_count || 0} produits</div>
                <div className="font-display text-xl uppercase truncate">{a.artist_name || a.name}</div>
                <div className="text-xs uppercase tracking-widest text-white/40 mt-1">{a.product_count} produits</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Sell CTA */}
      <section className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00] mb-4">Pilotes</div>
            <h2 className="font-display text-5xl md:text-7xl uppercase leading-none">Votre merch.<br />Vos fans.</h2>
            <p className="mt-6 text-white/70 max-w-md">
              Créez votre studio en 5 minutes. Publiez vos produits, gérez vos commandes, encaissez.
              Recevez vos paiements sécurisés <span className="text-[#FF3B00] font-bold">directement</span> auprès de vos fans.
            </p>
            <Link to="/register" data-testid="sell-cta" className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500]">
              Ouvrir mon studio <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Storefront, title: "Créez", text: "Configurez votre profil de pilote" },
              { icon: Package, title: "Publiez", text: "Ajoutez vos produits, tailles, couleurs" },
              { icon: Lightning, title: "Vendez", text: "Recevez vos commandes 24/7" },
              { icon: ArrowRight, title: "Encaissez", text: "Paiements sécurisés Stripe" },
            ].map((f, i) => (
              <div key={i} className="border border-white/10 p-6">
                <f.icon size={28} weight="bold" className="text-[#FF3B00]" />
                <div className="font-display text-2xl uppercase mt-4">{f.title}</div>
                <p className="text-sm text-white/60 mt-1">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
